import { Capacitor } from "@capacitor/core";
import { useAlarmOverlayStore } from "@/shared/state/alarmOverlayStore";

/**
 * 새 버전 배포 → 열려 있는 화면을 한 번 새로 고친다(2026-09-24).
 *
 * 서비스 워커(vite-plugin-pwa autoUpdate: skipWaiting + clientsClaim)는 새 sw.js 를 받자마자 활성화되지만,
 * **이미 떠 있는 화면은 옛 JS 그대로**다. 그래서 배포 뒤 새로고침을 두 번 해야 새 버전이 떴고(첫 번째는
 * 옛 캐시가 응답하고 그 사이 새 SW 가 설치된다), 켜 둔 관제 화면은 며칠씩 옛 버전이었다 —
 * 「웹 수정 배포한 거 맞아?」 지적의 원인. 이제
 *   · 새 SW 가 이 화면을 넘겨받으면(controllerchange) 한 번 새로 고친다.
 *     입력 중이면(입력칸·편집 영역에 포커스) 포커스가 빠질 때까지, 최대 10분 미룬다.
 *     미확인 경보창이 떠 있으면 그보다 먼저 창이 닫힐 때까지, 최대 60분 미룬다(2026-10-19, V-22).
 *     입력 중이라 기다리는 사이 경보창이 떠도 다시 미룬다(깨어날 때마다 두 조건을 다시 본다).
 *   · 켜 둔 화면도 30분마다 sw.js 를 다시 확인한다(브라우저는 페이지 이동 때만 확인한다).
 * ⛔ 첫 방문(원래 SW 가 없던 화면)의 clientsClaim 도 controllerchange 를 일으킨다 — 그때는 새 버전이
 *    아니므로 새로 고치지 않는다. 앱(Capacitor)은 자산이 설치본에 들어 있어 건너뛴다.
 */
const CHECK_EVERY_MS = 30 * 60_000;
const MAX_DEFER_MS = 10 * 60_000;
/**
 * 미확인 경보창(AlarmCenter)이 떠 있는 동안은 새로 고치지 않는다(2026-10-19, 10-12 검증 V-22) — 새로 고친 화면은
 * 열린 경보를 기준선에 흡수해 경보창·음성이 사라진다. 창이 닫히면(확인·해제) 그때 새로 고친다. 아무도 확인하지 않아
 * 창이 몇 시간씩 떠 있을 수도 있어 상한을 둔다(그때는 옛 버전으로 남는 것보다 새로 고치는 편을 택한다).
 */
const MAX_ALARM_DEFER_MS = 60 * 60_000;

function alarmOverlayOpen(): boolean {
  try {
    return useAlarmOverlayStore.getState().open;
  } catch {
    return false;
  }
}

function isEditing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function installServiceWorkerAutoRefresh(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (Capacitor.isNativePlatform()) return;
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  let reloaded = false;

  const reloadNow = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  /**
   * 새로 고쳐도 될 때까지 기다린다. **깨어날 때마다(포커스 빠짐·상한 타이머·경보창 닫힘) 두 조건을 처음부터 다시 본다**
   * — 입력 중이라 기다리는 사이 경보창이 뜨면, 경보창을 누르느라 포커스가 빠지는 순간이나 10분 상한 타이머가 그 창을
   * 확인도 안 한 채 새로 고치던 구멍을 막는다(2026-10-19 검증).
   *   1. 미확인 경보창이 떠 있으면 닫힐 때까지(처음 미룬 때부터 최대 MAX_ALARM_DEFER_MS).
   *   2. 입력 중이면 포커스가 빠질 때까지(이어서 입력 중인 동안 최대 MAX_DEFER_MS — 경보창이 끼면 새로 센다).
   */
  const reloadWhenSafe = () => {
    let alarmSince: number | null = null;
    let editSince: number | null = null;
    let cleanup = () => {};
    const step = () => {
      if (reloaded) return;
      cleanup();
      cleanup = () => {};
      const now = Date.now();
      if (alarmOverlayOpen()) {
        alarmSince ??= now;
        const left = alarmSince + MAX_ALARM_DEFER_MS - now;
        if (left > 0) {
          editSince = null;
          const unsub = useAlarmOverlayStore.subscribe((s) => { if (!s.open) step(); });
          const cap = setTimeout(step, left);
          cleanup = () => { unsub(); clearTimeout(cap); };
          return;
        }
      }
      if (isEditing()) {
        editSince ??= now;
        const left = editSince + MAX_DEFER_MS - now;
        if (left > 0) {
          let blurTimer: ReturnType<typeof setTimeout> | undefined;
          const onBlur = () => {
            clearTimeout(blurTimer);
            blurTimer = setTimeout(step, 1_000);
          };
          window.addEventListener("focusout", onBlur);
          const cap = setTimeout(step, left);
          cleanup = () => {
            window.removeEventListener("focusout", onBlur);
            clearTimeout(blurTimer);
            clearTimeout(cap);
          };
          return;
        }
      }
      reloadNow();
    };
    step();
  };

  let waiting = false;
  sw.addEventListener("controllerchange", () => {
    if (!hadController || waiting) return;            // 기다리는 사이 또 배포돼도 대기는 하나만
    waiting = true;
    reloadWhenSafe();
  });

  void sw.ready
    .then((reg) => {
      window.setInterval(() => { void reg.update().catch(() => undefined); }, CHECK_EVERY_MS);
    })
    .catch(() => undefined);
}
